import { NgModule, inject } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ACCESS_SUSPENDED_PATH } from '@pages/access-suspended/access-suspended.constants';
import { BILLING_PATH } from '@pages/billing/billing.constants';
import { COPILOT_ROUTE } from '@pages/copilot/copilot.route';
import { Authorities } from '@shared/constants/authorities';
import { LocalStorageConstants } from '@shared/constants/local-storage-constants';
import { PageSeoData } from '@shared/helpers/seo.service';
import { CanShowFullScreenCanActivateChildGuard } from 'app/guards/can-show-full-screen.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import {
  AuthCanActivateChildGuard,
  AuthCanActivateGuard,
  AuthCanMatchGuard,
} from './guards/auth.guard';
import { AuthoritiesCanActivateGuard } from './guards/authorities.guard';
import {
  CleanLocalStorageCanDeactivateGuard,
  CleanLocalStorageRouteData,
} from './guards/clean-local-storage-items.guard';
import { ManagerCanActivateChildGuard, ManagerCanMatchGuard } from './guards/manager.guard';
import { PaymentSuspendedCanActivateChildGuard } from './guards/payment-suspended.guard';
import { PortfoliosCanMatchGuard } from './guards/portfolios.guard';
import { SeoCanActivateChildGuard } from './guards/seo.guard';
import { SubscriptionCanMatchGuard } from './guards/subscription.guard';
import { TestCanActivateGuard } from './guards/test-site.guard';
import { LayoutRouteData, LayoutState } from './layout/layout.state';
import { LocalStoreResolver } from './resolvers/local-store.resolver';
import { LogoStateResolver } from './resolvers/logo-state.resolver';
import { UserActivityResolver } from './resolvers/user-activity.resolver';
import { UserStateResolver } from './resolvers/user-state.resolver';

/**
 * The app's route table. The /copilot entry is imported from COPILOT_ROUTE
 * (pages/copilot/copilot.route.ts) so its guards + entry authorities live with
 * the feature and a spec can assert the REAL route object without importing this
 * module (which statically references every lazy page module — a karma OOM).
 */
const routes: Routes = [
  {
    path: 'login',
    canActivateChild: [SeoCanActivateChildGuard, CanShowFullScreenCanActivateChildGuard],
    resolve: {
      logoState: LogoStateResolver,
    },
    loadChildren: async () => (await import('./pages/login/login.module')).LoginModule,
  },
  {
    path: 'privacy',
    canActivateChild: [SeoCanActivateChildGuard],
    loadChildren: async () => (await import('./pages/privacy/privacy.module')).PrivacyModule,
  },
  {
    path: 'pdf',
    canActivateChild: [SeoCanActivateChildGuard],
    resolve: {
      logoState: LogoStateResolver,
      userState: UserStateResolver,
    },
    loadChildren: async () => (await import('@pages/pdf/pdf.module')).PdfModule,
  },
  {
    path: 'terms-of-use',
    canActivateChild: [SeoCanActivateChildGuard],
    loadChildren: async () =>
      (await import('./pages/terms-of-use/terms-of-use.module')).TermsOfUseModule,
  },
  {
    path: 'cookie-policy',
    canActivateChild: [SeoCanActivateChildGuard],
    loadChildren: async () =>
      (await import('./pages/cookie-policy/cookie-policy.module')).CookiePolicyModule,
  },
  {
    path: 'test',
    component: LayoutComponent,
    canActivate: [TestCanActivateGuard],
    resolve: {
      logoState: LogoStateResolver,
    },
    loadChildren: async () => (await import('./pages/test-page/test-page.module')).TestPageModule,
  },
  {
    path: 'system-settings',
    component: LayoutComponent,
    canActivateChild: [
      ManagerCanActivateChildGuard,
      AuthCanActivateChildGuard,
      PaymentSuspendedCanActivateChildGuard,
      SeoCanActivateChildGuard,
      CanShowFullScreenCanActivateChildGuard,
    ],
    canMatch: [ManagerCanMatchGuard, AuthCanMatchGuard],
    resolve: {
      userActivity: UserActivityResolver,
      logoState: LogoStateResolver,
      userState: UserStateResolver,
      localStore: LocalStoreResolver,
    },
    data: {
      authorities: [Authorities.SettingsView],
      layout: {
        canShowFullScreen: false,
      } as LayoutRouteData,
    },
    loadChildren: async () =>
      (await import('./pages/system-settings/system-settings.module')).SystemSettingsModule,
  },
  {
    path: 'people-management',
    component: LayoutComponent,
    canActivate: [AuthoritiesCanActivateGuard],
    canActivateChild: [
      ManagerCanActivateChildGuard,
      AuthCanActivateChildGuard,
      PaymentSuspendedCanActivateChildGuard,
      SeoCanActivateChildGuard,
      CanShowFullScreenCanActivateChildGuard,
    ],
    canMatch: [ManagerCanMatchGuard, AuthCanMatchGuard],
    resolve: {
      userActivity: UserActivityResolver,
      logoState: LogoStateResolver,
      userState: UserStateResolver,
      localStore: LocalStoreResolver,
    },
    data: {
      layout: {
        canShowFullScreen: false,
      } as LayoutRouteData,
    },
    loadChildren: async () =>
      (await import('@pages/people-settings/people-settings.module')).PeopleSettingsModule,
  },
  {
    path: 'company-profile',
    component: LayoutComponent,
    canActivateChild: [
      ManagerCanActivateChildGuard,
      AuthCanActivateChildGuard,
      PaymentSuspendedCanActivateChildGuard,
      SeoCanActivateChildGuard,
      CanShowFullScreenCanActivateChildGuard,
    ],
    canMatch: [ManagerCanMatchGuard, AuthCanMatchGuard, SubscriptionCanMatchGuard],
    resolve: {
      userActivity: UserActivityResolver,
      logoState: LogoStateResolver,
      userState: UserStateResolver,
      localStore: LocalStoreResolver,
    },
    data: {
      authorities: [
        Authorities.CompaniesView,
        Authorities.LogotypeModify,
        Authorities.LogotypeDelete,
        Authorities.SiteInfoView,
        Authorities.SiteInfoModify,
      ],
      layout: {
        canShowFullScreen: false,
      } as LayoutRouteData,
    },
    loadChildren: async () =>
      (await import('./pages/company-settings/company-settings.module')).CompanySettingsModule,
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthCanActivateGuard],
    canActivateChild: [
      AuthCanActivateChildGuard,
      PaymentSuspendedCanActivateChildGuard,
      SeoCanActivateChildGuard,
      CanShowFullScreenCanActivateChildGuard,
    ],
    resolve: {
      userActivity: UserActivityResolver,
      logoState: LogoStateResolver,
      userState: UserStateResolver,
      localStore: LocalStoreResolver,
    },
    children: [
      {
        path: 'dashboard',
        canMatch: [AuthCanMatchGuard],
        data: {
          layout: {
            canShowFullScreen: false,
          } as LayoutRouteData,
        },
        loadChildren: async () =>
          (await import('./pages/dashboard/dashboard.module')).DashboardModule,
      },
      {
        path: BILLING_PATH,
        canMatch: [AuthCanMatchGuard],
        data: {
          authorities: [Authorities.BillingView],
          layout: {
            canShowFullScreen: false,
          } as LayoutRouteData,
        },
        loadChildren: async () => (await import('./pages/billing/billing.module')).BillingModule,
      },
      {
        path: 'positions',
        canMatch: [AuthCanMatchGuard],
        loadChildren: async () =>
          (await import('./pages/positions/positions.module')).PositionsModule,
      },
      {
        path: 'products',
        canMatch: [AuthCanMatchGuard],
        loadChildren: async () => (await import('./pages/products/products.module')).ProductsModule,
      },
      {
        path: 'transactions',
        canMatch: [AuthCanMatchGuard],
        canDeactivate: [CleanLocalStorageCanDeactivateGuard],
        data: {
          cleanLocalStorage: {
            items: [LocalStorageConstants.ConnectorsCurrentDateRange],
          } as CleanLocalStorageRouteData,
        },
        loadChildren: async () =>
          (await import('./pages/transactions/transactions.module')).TransactionsModule,
      },
      {
        path: 'instruments',
        canMatch: [AuthCanMatchGuard],
        loadChildren: async () =>
          (await import('./pages/instruments/instruments.module')).InstrumentsModule,
      },
      {
        path: 'screener',
        canMatch: [AuthCanMatchGuard, SubscriptionCanMatchGuard],
        data: {
          seo: {
            pageTitleI18nKey: 'pages.screener.title',
          } as PageSeoData,
          authorities: [Authorities.ModelPortfoliosView],
          bySubscription: true,
        },
        loadChildren: async () => (await import('./pages/screener/screener.module')).ScreenerModule,
      },
      {
        path: 'risks',
        canMatch: [AuthCanMatchGuard, SubscriptionCanMatchGuard],
        data: {
          authorities: [
            Authorities.MandateRiskProfileView,
            Authorities.RestrictionAlertView,
            Authorities.RiskReportView,
          ],
        },
        loadChildren: async () => (await import('./pages/risks/risks.module')).RisksModule,
      },
      COPILOT_ROUTE,
      {
        path: 'accounts',
        canMatch: [AuthCanMatchGuard],
        loadChildren: async () => (await import('./pages/accounts/accounts.module')).AccountsModule,
      },
      {
        path: 'portfolios',
        canMatch: [AuthCanMatchGuard, PortfoliosCanMatchGuard],
        loadChildren: async () =>
          (await import('./pages/portfolios/portfolios.module')).PortfoliosModule,
      },
      {
        path: 'reports',
        canMatch: [AuthCanMatchGuard],
        loadChildren: async () => (await import('./pages/reports/reports.module')).ReportsModule,
      },
      {
        path: 'detail',
        canMatch: [AuthCanMatchGuard],
        data: {
          layout: {
            canShowFullScreen: true,
          } as LayoutRouteData,
        },
        loadChildren: async () => (await import('./pages/detail/detail.module')).DetailModule,
      },
      {
        path: 'marketplace',
        canMatch: [AuthCanMatchGuard, SubscriptionCanMatchGuard],
        data: {
          seo: {
            pageTitleI18nKey: 'marketplace.title',
          } as PageSeoData,
          breadcrumb: '',
          authorities: [Authorities.MarketplaceView],
          bySubscription: true,
        },
        loadChildren: async () =>
          (await import('./marketplace/marketplace.module')).MarketplaceModule,
      },
      {
        path: 'import',
        canMatch: [AuthCanMatchGuard],
        loadChildren: async () =>
          (await import('@pages/import/import-page.module')).ImportPageModule,
      },
      {
        path: 'reconciliation',
        canMatch: [AuthCanMatchGuard],
        loadChildren: async () =>
          (await import('./pages/reconciliation/reconciliation.module')).ReconciliationModule,
      },
      {
        path: 'retail',
        canMatch: [AuthCanMatchGuard],
        loadChildren: async () => (await import('./pages/retail/retail.module')).RetailModule,
      },
      {
        // Where the gateway lands the browser after linking a Google / Microsoft account; the page
        // only opens the user-settings dialog on its Connected accounts tab.
        path: 'settings/connected-accounts',
        canMatch: [AuthCanMatchGuard],
        data: {
          seo: {
            pageTitleI18nKey: 'pages.connected-accounts.title',
          } as PageSeoData,
          layout: {
            canShowFullScreen: false,
          } as LayoutRouteData,
        },
        loadChildren: async () =>
          (await import('./pages/connected-accounts/connected-accounts-page.module'))
            .ConnectedAccountsPageModule,
      },
      {
        // Inside the layout on purpose, so the navbar still frames the screen — the suspension
        // guard lets this one path through, and NavbarComponent dims the navigation on it.
        path: ACCESS_SUSPENDED_PATH,
        canMatch: [AuthCanMatchGuard],
        data: {
          layout: {
            canShowFullScreen: false,
          } as LayoutRouteData,
        },
        loadComponent: () =>
          import('./pages/access-suspended/access-suspended.component').then(
            (c) => c.AccessSuspendedComponent,
          ),
      },
      {
        path: '**',
        canDeactivate: [
          () => {
            inject(LayoutState).subscriptionNotificationData$.next(null);
            return true;
          },
        ],
        loadComponent: () =>
          import('./pages/not-found/not-found.component').then((c) => c.NotFoundComponent),
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top',
    }),
  ],
})
export class AppRoutingModule {}
